/**
 * MFA Login Verification Dialog
 *
 * Shown after Keycloak authentication when user has MFA enabled.
 * Supports both TOTP codes (6 digits) and backup codes (alphanumeric).
 */

import { useState, useEffect, useRef } from 'react'
import { Loader2, Shield, KeyRound, Key } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface MfaLoginDialogProps {
  open: boolean
  userEmail?: string
  onVerify: (code: string, isBackupCode: boolean) => Promise<boolean>
  onCancel: () => void
}

export function MfaLoginDialog({
  open,
  userEmail,
  onVerify,
  onCancel,
}: MfaLoginDialogProps) {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [useBackupCode, setUseBackupCode] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when dialog opens or mode changes
  useEffect(() => {
    if (open) {
      setCode('')
      setError(null)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open, useBackupCode])

  // Handle TOTP code input - only digits
  const handleTotpCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
    setError(null)
  }

  // Handle backup code input - alphanumeric with dash
  const handleBackupCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow alphanumeric and dash, convert to uppercase
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, '')
      .slice(0, 9) // XXXX-XXXX format
    setCode(value)
    setError(null)
  }

  // Validate code based on mode
  const isCodeValid = useBackupCode
    ? code.length >= 8 // Backup code: at least 8 chars (XXXX-XXXX or XXXXXXXX)
    : code.length === 6 // TOTP: exactly 6 digits

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isCodeValid) {
      setError(
        useBackupCode
          ? 'Please enter a valid backup code (e.g., AF18-BF09)'
          : 'Please enter a 6-digit code'
      )
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const success = await onVerify(code, useBackupCode)
      if (!success) {
        setError(
          useBackupCode
            ? 'Invalid backup code. Please try again.'
            : 'Invalid verification code. Please try again.'
        )
        setCode('')
        inputRef.current?.focus()
      }
    } catch {
      setError('Verification failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle key press for Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isCodeValid && !isLoading) {
      handleSubmit(e)
    }
  }

  // Toggle between TOTP and backup code mode
  const toggleMode = () => {
    setUseBackupCode(!useBackupCode)
    setCode('')
    setError(null)
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-primary/10">
              {useBackupCode ? (
                <Key className="h-6 w-6 text-primary" />
              ) : (
                <Shield className="h-6 w-6 text-primary" />
              )}
            </div>
            <DialogTitle>
              {useBackupCode ? 'Use Backup Code' : 'Two-Factor Authentication'}
            </DialogTitle>
          </div>
          <DialogDescription>
            {useBackupCode
              ? 'Enter one of your backup codes'
              : 'Enter the verification code from your authenticator app'}
            {userEmail && (
              <span className="block mt-1 text-sm">
                Logging in as <strong>{userEmail}</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="mfa-code">
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                {useBackupCode ? (
                  <Input
                    ref={inputRef}
                    id="mfa-code"
                    type="text"
                    maxLength={9}
                    placeholder="XXXX-XXXX"
                    value={code}
                    onChange={handleBackupCodeChange}
                    onKeyDown={handleKeyDown}
                    className="pl-10 text-center text-xl tracking-widest font-mono uppercase"
                    disabled={isLoading}
                    autoComplete="off"
                  />
                ) : (
                  <Input
                    ref={inputRef}
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={handleTotpCodeChange}
                    onKeyDown={handleKeyDown}
                    className="pl-10 text-center text-2xl tracking-widest font-mono"
                    disabled={isLoading}
                    autoComplete="one-time-code"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {useBackupCode
                  ? 'Backup codes are single-use. Once used, it cannot be used again.'
                  : 'Open your authenticator app to view your verification code'}
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            {/* Toggle between TOTP and backup code */}
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={toggleMode}
                disabled={isLoading}
                className="text-muted-foreground hover:text-primary"
              >
                {useBackupCode
                  ? 'Use authenticator app instead'
                  : "Lost access to your app? Use a backup code"}
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !isCodeValid}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default MfaLoginDialog
